import { CircleCheck, TriangleAlert } from '@/lib/icons'
import type { PayoutBulkResult } from '@ayub/shared'
import { formatNumber, formatSom } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'

const SKIP_REASON_LABELS: Record<PayoutBulkResult['skipped'][number]['reason'], string> = {
  no_card: "karta yo'q",
  no_balance: "balans yo'q qoldi",
  already_requested: "kutayotgan so'rovi bor",
}

export interface BulkPayResultDialogProps {
  result: PayoutBulkResult | null
  bloggerNames: Map<number, string>
  onClose: () => void
}

/**
 * Ommaviy to'lov natijasi — vazifa talabi so'zma-so'z: "45 ta to'landi, 3 tasi o'tkazib
 * yuborildi (karta yo'q)" — GENERIK "muvaffaqiyatli" xabari EMAS, chunki "admin 48 kishiga
 * to'ladim deb o'ylab, aslida 45 ga to'lagan bo'lsa — buni g'azablangan blogerdan bilib oladi".
 * Shuning uchun bu oyna doim `created` VA `skipped` ikkalasini ham ko'rsatadi, natija
 * to'liq muvaffaqiyatli bo'lsa ham.
 */
export function BulkPayResultDialog({ result, bloggerNames, onClose }: BulkPayResultDialogProps) {
  if (!result) return null
  const hasSkipped = result.skipped.length > 0

  return (
    <Modal
      open={result !== null}
      onClose={onClose}
      ariaLabel="Ommaviy to'lov natijasi"
      size="compact"
      zIndex={50}
    >
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-semibold">To&apos;lov yakunlandi</h2>

        <div className="flex items-start gap-2 rounded-card border border-urgency-done/30 bg-urgency-done/10 p-3">
          <CircleCheck
            size={18}
            className="mt-0.5 shrink-0 text-urgency-done-text"
            aria-hidden="true"
          />
          <p className="text-sm text-app-text">
            <span className="tabular font-semibold">{formatNumber(result.created)} ta</span>{' '}
            blogerga jami{' '}
            <span className="tabular font-semibold">{formatSom(result.totalAmount)}</span>{' '}
            to&apos;landi.
          </p>
        </div>

        {hasSkipped && (
          <div className="flex flex-col gap-2 rounded-card border border-urgency-today/30 bg-urgency-today/10 p-3">
            <p className="inline-flex items-center gap-2 text-sm font-medium text-urgency-today-text">
              <TriangleAlert size={16} aria-hidden="true" />
              {formatNumber(result.skipped.length)} tasi o&apos;tkazib yuborildi
            </p>
            <ul className="flex flex-col gap-1 text-xs text-app-text">
              {result.skipped.map((item) => (
                <li key={item.bloggerId} className="flex items-center justify-between gap-2">
                  <span>{bloggerNames.get(item.bloggerId) ?? `#${item.bloggerId}`}</span>
                  <span className="text-app-hint">{SKIP_REASON_LABELS[item.reason]}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button onClick={onClose}>Tushunarli</Button>
        </div>
      </div>
    </Modal>
  )
}
